import * as React from "react";
import { MailCheck } from "lucide-react";
import { forgotPasswordSchema } from "@taskiq/shared";
import { AuthLayout } from "@/components/AuthLayout";
import { TurnstileWidget } from "@/components/auth/TurnstileWidget";
import { Button } from "@/components/ui/Button";
import { Field, Input, Form } from "@/components/ui/Field";
import { FormMessage } from "@/components/ui/Feedback";
import { useAuth } from "@/lib/auth-context";
import { describeAuthError } from "@/lib/auth-errors";
import { PLACEHOLDER } from "@/lib/placeholders";
import { useTurnstile } from "@/lib/turnstile";

const RESEND_COOLDOWN_SECONDS = 60;

export function ForgotPassword() {
  const { sendPasswordReset } = useAuth();

  const [email, setEmail] = React.useState("");
  const [fieldError, setFieldError] = React.useState<string | undefined>();
  const [formError, setFormError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);
  const captcha = useTurnstile();

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  async function submit() {
    setFormError(null);

    // The shared schema, not a local regex - same rule the sign-up and invite
    // forms apply, so an address rejected here is rejected everywhere.
    const parsed = forgotPasswordSchema.safeParse({ email });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? "Enter a valid email address");
      return;
    }
    setFieldError(undefined);
    setSubmitting(true);

    try {
      await sendPasswordReset(email, captcha.token);
      setSent(true);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setFormError(describeAuthError(err));
    } finally {
      // On both paths, not just the failure. A success moves to the sent screen
      // whose Resend button calls this again, and it would be calling it with a
      // token GoTrue has already spent.
      captcha.reset();
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout
        title="Check your email"
        // Note the wording: "if an account exists". Confirming whether an address
        // is registered turns this form into an account-enumeration oracle.
        subtitle={`If an account exists for ${email.trim()}, a reset link is on its way.`}
        back={{ to: "/login", label: "Back to sign in" }}
      >
        <div className="auth-stack">
          <div className="auth-callout auth-callout--ok">
            <MailCheck />
            The link opens a page where you can set a new password. It expires in one hour.
          </div>

          {/* Resend calls `submit` again, which is a second email against the
              project's shared send quota - so it is challenged like the first.
              `interaction-only` keeps that check without a bordered Cloudflare
              box sitting on a screen that has no form on it - it only becomes
              visible if Cloudflare actually needs to interrogate this visitor. */}
          <TurnstileWidget
            onToken={captcha.onToken}
            onUnavailable={captcha.onUnavailable}
            resetKey={captcha.resetKey}
            appearance="interaction-only"
          />

          <Button
            variant="ghost"
            size="md"
            disabled={cooldown > 0}
            loading={submitting}
            onClick={submit}
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend link"}
          </Button>

          <FormMessage>{formError}</FormMessage>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a link to set a new one."
      back={{ to: "/login", label: "Back to sign in" }}
    >
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Work email" htmlFor="email" required error={fieldError}>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="username email"
            autoFocus
            placeholder={PLACEHOLDER.email}
            invalid={Boolean(fieldError)}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        <FormMessage>{formError}</FormMessage>

        <TurnstileWidget
          onToken={captcha.onToken}
          onUnavailable={captcha.onUnavailable}
          resetKey={captcha.resetKey}
        />

        <Button type="submit" size="lg" block loading={submitting} className="auth-submit">
          Send reset link
        </Button>
      </Form>
    </AuthLayout>
  );
}
